import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ThunkAction, Action } from '@reduxjs/toolkit';
import type { MasterFlightRecord } from '../../../utils/types';
import { makeNewRecord } from '../../../utils/types';

interface FormState {
  isOpen: boolean;
  editMode: boolean;
  isVariantMode: boolean;
  isAdhocMode: boolean;
  activeTab: 'basic' | 'stations' | 'preview';
  draft: MasterFlightRecord;
}

const initialState: FormState = {
  isOpen: false,
  editMode: false,
  isVariantMode: false,
  isAdhocMode: false,
  activeTab: 'basic',
  draft: makeNewRecord(),
};

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    openCreateForm: (state) => {
      state.isOpen = true;
      state.editMode = false;
      state.isVariantMode = false;
      state.isAdhocMode = false;
      state.activeTab = 'basic';
      state.draft = makeNewRecord();
    },

    openVariantForm: (state, action: PayloadAction<MasterFlightRecord>) => {
      state.isOpen = true;
      state.editMode = false;
      state.isVariantMode = true;
      state.isAdhocMode = false;
      state.activeTab = 'basic';
      state.draft = action.payload;
    },

    openEditForm: (state, action: PayloadAction<MasterFlightRecord>) => {
      state.isOpen = true;
      state.editMode = true;
      state.isVariantMode = false;
      state.isAdhocMode = false;
      state.activeTab = 'basic';
      state.draft = action.payload;
    },

    openAdhocForm: (state) => {
      state.isOpen = true;
      state.editMode = false;
      state.isVariantMode = false;
      state.isAdhocMode = true;
      state.activeTab = 'basic';
      state.draft = { ...makeNewRecord(), isAdhoc: true };
    },

    closeForm: (state) => {
      state.isOpen = false;
    },

    setActiveTab: (state, action: PayloadAction<'basic' | 'stations' | 'preview'>) => {
      state.activeTab = action.payload;
    },

    setDraft: (state, action: PayloadAction<MasterFlightRecord>) => {
      state.draft = action.payload;
    },
  },
});

export const {
  openCreateForm,
  openVariantForm,
  openEditForm,
  openAdhocForm,
  closeForm,
  setActiveTab,
  setDraft,
} = formSlice.actions;

export default formSlice.reducer;

// Thunks that support React.SetStateAction (updater function) pattern
type FormThunk = ThunkAction<void, { form: FormState }, unknown, Action>;

export const setActiveTabAction = (
  tab: React.SetStateAction<'basic' | 'stations' | 'preview'>
): FormThunk => (dispatch, getState) => {
  if (typeof tab === 'function') {
    dispatch(setActiveTab(tab(getState().form.activeTab)));
  } else {
    dispatch(setActiveTab(tab));
  }
};

export const setDraftAction = (
  newDraft: React.SetStateAction<MasterFlightRecord>
): FormThunk => (dispatch, getState) => {
  if (typeof newDraft === 'function') {
    dispatch(setDraft(newDraft(getState().form.draft)));
  } else {
    dispatch(setDraft(newDraft));
  }
};
